import React, { useEffect, useRef, useState } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import { TERMS_AND_CONDITIONS_VERSION } from "../../constants";
import PropTypes from "prop-types";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";

const SignupOtherClientsSubmission = ({ signupOtherClientsFlowState, onFlowComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref guard prevents StrictMode double-mount from firing a second request
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    const payload = {
      clientId: signupOtherClientsFlowState?.clientId,
      termsAndConditionsVersion: TERMS_AND_CONDITIONS_VERSION,
      clientName: signupOtherClientsFlowState?.clientName?.client,
      clientDetails: signupOtherClientsFlowState?.clientDetails?.client,
      employmentInformation: signupOtherClientsFlowState?.employmentInformation,
      clientAddress: signupOtherClientsFlowState?.clientAddress,
    };

    const isEmptySubmission =
      payload.clientName == null &&
      payload.clientDetails == null &&
      payload.employmentInformation == null &&
      payload.clientAddress == null;

    if (isEmptySubmission) {
      setError("No information provided");
      setLoading(false);
      return;
    }

    fetch("/api/clientSignup", {
      method: "PUT",
      body: JSON.stringify(payload),
    })
      .then(
        async (resp) => {
          if (resp.ok) {
            setLoading(false);
            onFlowComplete();
          } else {
            const body = await resp.text().catch(() => "");
            console.error("Signup other clients submission failed:", resp.status, body);
            captureSubmissionError("SignupOtherClients", resp.status, body);
            setError("Unable to process your signup");
            setLoading(false);
          }
        },
        (err) => {
          console.error("Signup other clients submission failed:", err);
          setError("Unable to process your signup");
          setLoading(false);
        },
      )
      .catch((err) => {
        console.error("Signup other clients submission failed:", err);
        setError("Unable to process your signup");
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- submit once on mount; ref guard prevents StrictMode re-fire

  if (loading) {
    return <SubmittingCard />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

SignupOtherClientsSubmission.propTypes = {
  signupOtherClientsFlowState: PropTypes.shape({
    clientId: PropTypes.string,
    clientName: PropTypes.shape({
      client: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
    clientDetails: PropTypes.shape({
      client: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
    employmentInformation: PropTypes.object,
    clientAddress: PropTypes.object,
  }).isRequired,
  onFlowComplete: PropTypes.func.isRequired,
};

export default SignupOtherClientsSubmission;
